import { ProgressIndicatorType } from "@/types/ProgressIndicator";
import { CloneScenarioRequest, Scenario } from "@api/apiClient";
import { useProfiles } from "@api/profiles";
import { useCloneScenario } from "@api/scenarios";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { Box, Button, Card, CardContent, CardHeader, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography, useTheme } from "@mui/material";
import _ from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";

interface CloneScenarioFormProps {
  formData: {
    scenarioId: string;
    name: string;
    description: string;
    rebarEstimation: boolean;
    profileId?: string;
  };
  scenarios: Scenario[];
  handleCloneScenarioClose: () => void;
}

const CloneScenarioForm = observer((props: CloneScenarioFormProps) => {
  const theme = useTheme();
  const appStore = useAppStore();
  const [formData, setFormData] = useState(props.formData);
  const [duplicateScenarioError, setDuplicateScenarioError] = useState(false);

  const cloneScenario = useCloneScenario();
  const { data: profileData } = useProfiles();

  // Match profileId with profile name
  const profileName = profileData?.find((profile) => profile.profileId === formData.profileId)?.name;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    validateDuplicateScenario(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    cloneScenario.mutateAsync(
      new CloneScenarioRequest({
        scenarioId: formData.scenarioId,
        name: formData.name,
        description: formData.description,
        rebarEstimation: formData.rebarEstimation,
        profileId: formData.profileId,
      }) as any,
    );
    appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CloneScenario, true);
    props.handleCloneScenarioClose();
  };

  const validateDuplicateScenario = (scanarioName: string) => {
    const finddata = props.scenarios?.map((x) => x.name).filter((x) => x?.toLocaleLowerCase() == scanarioName.trim().toLocaleLowerCase());
    if (finddata && finddata?.length > 0) {
      setDuplicateScenarioError(true);
    } else {
      setDuplicateScenarioError(false);
    }
  };

  useEffect(() => {
    validateDuplicateScenario(formData.name);
  }, [props.formData, props.scenarios]);

  return (
    <form onSubmit={handleSubmit}>
      <Stack mt={1} spacing={2}>
        <TextField
          label={i18n.t("scenarios-grid.clone-dialog.name-input.title")}
          name="name"
          type="text"
          value={formData.name}
          onChange={handleInputChange}
          margin="normal"
          variant="outlined"
          fullWidth
          required
          error={(cloneScenario.isPending && _.isEmpty(formData.name?.trim())) || duplicateScenarioError}
          helperText={
            cloneScenario.isPending && _.isEmpty(formData.name?.trim())
              ? i18n.t("scenario.helper.scenario-name")
              : duplicateScenarioError
                ? i18n.t("scenarios-grid.clone-dialog.name-input.duplicate")
                : ""
          }
        />
        <TextField
          label={i18n.t("scenarios-grid.clone-dialog.description-input.title")}
          name="description"
          type="text"
          value={formData.description}
          onChange={handleInputChange}
          margin="normal"
          variant="outlined"
          fullWidth
          required
          error={cloneScenario.isPending && _.isEmpty(formData.description?.trim())}
          helperText={cloneScenario.isPending && _.isEmpty(formData.description?.trim()) ? i18n.t("scenario.helper.scenario-description") : ""}
        />
        <FormControl fullWidth margin="normal" disabled>
          <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.profile-input.title")}</InputLabel>
          <Select value={formData.profileId ?? ""} label={i18n.t("scenarios-tab.scenario-dialog.profile-input.title")}>
            <MenuItem value={formData.profileId ?? ""}>{profileName ?? i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder")}</MenuItem>
          </Select>
        </FormControl>

        <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
          <CardHeader title={<Typography variant="body2">{i18n.t("scenarios-tab.title.scenario-settings")}</Typography>} elevation={3} />
          <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1">{i18n.t("scenarios-tab.title.rebar-estimation")}</Typography>
              <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                {i18n.t("scenarios-tab.description.rebar-estimation")}
              </Typography>
            </Box>
            <Switch checked={props.formData.rebarEstimation} disabled={true} />
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Grid container spacing={2} sx={{ justifyContent: "end" }}>
            <Button autoFocus loading={cloneScenario.isPending} disabled={duplicateScenarioError} variant="contained" type="submit">
              {i18n.t("scenarios-grid.clone-dialog.clone-button.title")}
            </Button>
          </Grid>
        </Box>
      </Stack>
    </form>
  );
});

export default CloneScenarioForm;
